import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { 
  Users, BookOpen, MessageCircle, Star, TrendingUp, Lightbulb,
  BarChart3, Activity, FileText, GraduationCap, Video
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [courseStats, setCourseStats] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [overviewRes, coursesRes, feedbackRes, activityRes] = await Promise.all([
        axios.get(`${API}/stats/overview`),
        axios.get(`${API}/stats/courses`),
        axios.get(`${API}/stats/feedback`),
        axios.get(`${API}/stats/activity`, { params: { days: 30 } })
      ]);
      setOverview(overviewRes.data);
      setCourseStats(coursesRes.data);
      setFeedbackStats(feedbackRes.data);
      setActivityStats(activityRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Statistiken");
    } finally {
      setLoading(false);
    }
  };

  const StarDisplay = ({ rating, maxStars = 5 }) => (
    <div className="flex items-center gap-1">
      {[...Array(maxStars)].map((_, i) => (
        <Star 
          key={i} 
          className={`h-4 w-4 ${i < Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`}
        />
      ))}
      <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout title="Statistiken">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Statistik-Dashboard">
      <div className="space-y-6" data-testid="stats-dashboard">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="courses">Kurse</TabsTrigger>
            <TabsTrigger value="feedback">Feedback</TabsTrigger>
            <TabsTrigger value="activity">Aktivität</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold">{overview?.users?.total || 0}</p>
                      <p className="text-sm text-muted-foreground">Benutzer gesamt</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold">{overview?.courses?.total || 0}</p>
                      <p className="text-sm text-muted-foreground">Kurse</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <MessageCircle className="h-6 w-6 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold">{overview?.community?.total_posts || 0}</p>
                      <p className="text-sm text-muted-foreground">Community-Beiträge</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                      <Star className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold">{overview?.feedback?.average_rating || 0}</p>
                      <p className="text-sm text-muted-foreground">Ø Bewertung</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* User Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Benutzer nach Rolle
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {overview?.users?.by_role && Object.entries(overview.users.by_role).map(([role, count]) => {
                    const total = overview.users.total;
                    const percent = total > 0 ? (count / total * 100) : 0;
                    const roleLabels = {
                      participant: "Teilnehmer",
                      dozent: "Dozenten",
                      admin: "Admins",
                      super_admin: "Super-Admins",
                      auditor: "Auditoren"
                    };
                    return (
                      <div key={role} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{roleLabels[role] || role}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                        <Progress value={percent} className="h-2" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Kurse nach Kategorie
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {overview?.courses?.by_category && Object.entries(overview.courses.by_category).map(([cat, count]) => {
                    const total = overview.courses.total;
                    const percent = total > 0 ? (count / total * 100) : 0;
                    return (
                      <div key={cat} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{cat}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                        <Progress value={percent} className="h-2" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-card border-border">
                <CardContent className="p-4 text-center">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">{overview?.courses?.total_content || 0}</p>
                  <p className="text-xs text-muted-foreground">Lerninhalte</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardContent className="p-4 text-center">
                  <Video className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">{overview?.media?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Medien</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardContent className="p-4 text-center">
                  <GraduationCap className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">{overview?.learning?.total_completions || 0}</p>
                  <p className="text-xs text-muted-foreground">Abschlüsse</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardContent className="p-4 text-center">
                  <Lightbulb className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-2xl font-bold">{overview?.kvp?.pending || 0}</p>
                  <p className="text-xs text-muted-foreground">Offene KVP</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="mt-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Kursstatistiken</CardTitle>
                <CardDescription>Übersicht aller Kurse mit Nutzungsdaten</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kurs</TableHead>
                      <TableHead>Kategorie</TableHead>
                      <TableHead className="text-center">Eingeschrieben</TableHead>
                      <TableHead className="text-center">Inhalte</TableHead>
                      <TableHead className="text-center">Abschlussrate</TableHead>
                      <TableHead className="text-center">Bewertung</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {courseStats.map(course => (
                      <TableRow key={course.course_id}>
                        <TableCell className="font-medium">{course.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{course.category}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{course.enrolled_users}</TableCell>
                        <TableCell className="text-center">{course.content_items}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Progress value={course.completion_rate} className="w-16 h-2" />
                            <span className="text-sm">{course.completion_rate}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {course.feedback_count > 0 ? (
                            <StarDisplay rating={course.average_rating} />
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {courseStats.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          Keine Kursdaten vorhanden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="mt-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Feedback-Auswertung</CardTitle>
                <CardDescription>Detaillierte Bewertungen pro Kurs</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kurs</TableHead>
                      <TableHead className="text-center">Bewertungen</TableHead>
                      <TableHead className="text-center">Gesamt</TableHead>
                      <TableHead className="text-center">Verständlichkeit</TableHead>
                      <TableHead className="text-center">Praxisbezug</TableHead>
                      <TableHead className="text-center">Qualität</TableHead>
                      <TableHead className="text-center">Material</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbackStats.map(course => (
                      <TableRow key={course.course_id}>
                        <TableCell className="font-medium">{course.title}</TableCell>
                        <TableCell className="text-center">{course.total_reviews}</TableCell>
                        <TableCell className="text-center">
                          <StarDisplay rating={course.average_overall} />
                        </TableCell>
                        <TableCell className="text-center">
                          {course.average_clarity > 0 ? course.average_clarity.toFixed(1) : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {course.average_practical > 0 ? course.average_practical.toFixed(1) : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {course.average_quality > 0 ? course.average_quality.toFixed(1) : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {course.average_materials > 0 ? course.average_materials.toFixed(1) : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {feedbackStats.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          Noch keine Bewertungen vorhanden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Aktivität der letzten {activityStats?.period_days || 30} Tage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold text-blue-500">{activityStats?.new_users || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Neue Benutzer</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold text-green-500">{activityStats?.course_completions || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Kursabschlüsse</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold text-purple-500">{activityStats?.new_posts || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Community-Beiträge</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold text-yellow-500">{activityStats?.new_feedback || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">Neue Bewertungen</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <p className="text-3xl font-bold text-orange-500">{activityStats?.new_kvp || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">KVP-Einträge</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default StatsDashboard;
